/** Controlled-rollout media landing pages: discover the product without implying public catalogs or funded attention. */
import { useEffect } from "react";
import {
  Play,
  Music2,
  Bookmark,
  SlidersHorizontal,
  ShieldCheck,
} from "lucide-react";
import { Action, MediaCards } from "./ConnectedHome";
export default function MediaExperience({ kind }) {
  const tube = kind === "tube",
    name = tube ? "KwK Tube" : "KwK Tunes";
  useEffect(() => {
    document.title = `${name} | A little more to discover in KwK`;
    document
      .querySelector('meta[name="description"]')
      ?.setAttribute(
        "content",
        `${name} brings ${tube ? "video" : "music and audio"} into the KwK ecosystem. Explore the controlled creator rollout, playback experience and how to get involved.`,
      );
  }, [name, tube]);
  return (
    <main id="main-content" className="cw-site cw-media-page">
      <section className="cw-wrap cw-section">
        <div className="cw-section-head">
          <div>
            <span className="cw-badge">CONTROLLED CREATOR ROLLOUT</span>
            <span className="cw-kicker">{name.toUpperCase()}</span>
            <h1>
              {tube ? (
                <>
                  A fresh perspective.
                  <br />
                  Right here in KwK.
                </>
              ) : (
                <>
                  Give your day
                  <br />a different soundtrack.
                </>
              )}
            </h1>
            <p>
              {tube
                ? "The useful idea. The story that stays with you. KwK Tube is taking shape as a home for video and its creators, alongside the shopping, work and play already in your KwK world."
                : "Music, spoken stories and something new to discover. KwK Tunes is taking shape as a home for audio and its creators—one more way to make KwK part of your day."}
            </p>
            <div className="cw-actions">
              <Action to="/#join" role="buyer">
                Ask about access
              </Action>
              <a
                className="cw-action cw-secondary"
                href="mailto:admin@kwknet.com?subject=KwK%20creator%20inquiry"
              >
                Email the creator team
              </a>
            </div>
          </div>
        </div>
        <MediaCards currentKind={kind} />
        <div className="cw-media-features">
          {[
            [
              tube ? Play : Music2,
              tube ? "Made for watching" : "Made for listening",
              tube
                ? "Explore the video playback experience as eligible creator releases become available."
                : "Explore audio playback and revisit recent listening as eligible releases become available.",
            ],
            [
              Bookmark,
              "Pick up where you left off",
              "Find your channels, releases and recent playback together. Catalog access depends on your account and the current rollout.",
            ],
            [
              ShieldCheck,
              "A considered creator rollout",
              "Creators follow rights and review steps before a release is published. Ask the KwK team about current access and requirements.",
            ],
          ].map(([Icon, title, body]) => (
            <article key={title}>
              <Icon size={28} />
              <h3>{title}</h3>
              <p>{body}</p>
            </article>
          ))}
        </div>
      </section>
      <section id="media-faq" className="cw-wrap cw-section">
        <span className="cw-kicker">A FEW THINGS TO KNOW</span>
        <h2>Curious? Start here.</h2>
        <details>
          <summary>Can everyone use {name} today?</summary>
          <p>
            The media experience is in a controlled rollout. Access, catalog and
            features depend on account eligibility and availability. Contact KwK
            for the current options.
          </p>
        </details>
        <details>
          <summary>
            Does watching or listening automatically earn money?
          </summary>
          <p>
            No. Playback does not automatically earn money or points. Any
            eligible sponsored mission needs its own published terms, funding
            and server-confirmed qualification. No cash or reward is promised on
            this page.
          </p>
        </details>
        <details>
          <summary>How can I publish something?</summary>
          <p>
            Ask about creator access. Approved creators use Creator Studio to
            manage their own drafts, rights, review and publication. This
            website does not upload or publish your work.
          </p>
        </details>
        <details>
          <summary>Is this a separate app?</summary>
          <p>
            Tube and Tunes are part of the KwK app ecosystem. Their rollout does
            not change your launcher, shopping or existing account roles.
          </p>
        </details>
      </section>
    </main>
  );
}
